import { useEffect, useRef } from 'react'

type FbqArgs = Record<string, any>

/**
 * Fire a standard Meta Pixel event (e.g. PageView, ViewContent, InitiateCheckout).
 * Safe to call before fbq is loaded — events queue up automatically.
 */
export function track(event: string, data?: FbqArgs) {
  if (typeof window === 'undefined') return
  const fbq = (window as any).fbq
  if (typeof fbq === 'function') {
    fbq('track', event, data)
  }
}

/**
 * Fire a custom Meta Pixel event (not in Meta's standard event list).
 */
export function trackCustom(event: string, data?: FbqArgs) {
  if (typeof window === 'undefined') return
  const fbq = (window as any).fbq
  if (typeof fbq === 'function') {
    fbq('trackCustom', event, data)
  }
}

/**
 * Returns a ref that, when attached to an element, fires a Meta Pixel
 * `ViewContent` event the first time at least 30% of the element is visible.
 *
 * Useful for tracking which sections of the page users actually see.
 */
export function useSectionView(sectionName: string, threshold: number = 0.3) {
  const ref = useRef<HTMLElement | null>(null)
  const firedRef = useRef(false)

  useEffect(() => {
    const node = ref.current
    if (!node || firedRef.current) return
    if (typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true
            track('ViewContent', {
              content_name: sectionName,
              content_category: 'Nutrición Carrera Resuelta',
            })
            observer.disconnect()
            break
          }
        }
      },
      { threshold },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [sectionName, threshold])

  return ref as React.MutableRefObject<any>
}
